/**
 * media-audio-probe.js — read the AUDIO codec out of a container header,
 * without loading FFmpeg.
 *
 * Why this exists
 * ---------------
 * universal-video-decode's canPlayNatively() proves a VIDEO FRAME reached the
 * compositor. It says nothing about sound, and it runs the probe muted. So a
 * file whose video decodes but whose audio does not was accepted on the
 * `native` tier and played SILENTLY — no error, no telemetry, tool reports
 * success. Measured 2026-08-05 with tests/playwright/codec-audio-probe:
 *
 *     Chromium  AC-3 / E-AC-3 / DTS / TrueHD → canPlayType "" (refused)
 *     Firefox   AC-3 / E-AC-3 / DTS / TrueHD → canPlayType "" (refused)
 *     WebKit    says "probably" to ALL of them, including DTS/TrueHD
 *
 * H.264 + AC-3 in MKV is *the* standard movie rip, so this was the silent
 * "picture but no sound" case. canPlayType is therefore used only as a
 * NEGATIVE signal (WebKit's "probably" is not evidence), and the real answer
 * comes from reading the container's own codec id.
 *
 *   await MediaAudioProbe.probe(file)
 *     → { hasAudio, codecs:['ac-3'], playable:false, reason:'ac-3', source:'mp4',
 *         channels:6, sampleRate:48000, durationSec:213.4 }
 *
 * `playable:null` means "could not tell" — callers must treat that as "carry
 * on as before", never as a failure. `hasAudio:null` is the same «could not
 * tell» for the presence question: only a container that was actually read
 * answers `false` (see probe()). `channels` / `sampleRate` / `durationSec`
 * are 0 when the header did not carry them; 0 means UNKNOWN, never "none".
 *
 * Why the layout numbers live here too (2026-08-29)
 * -------------------------------------------------
 * AudioHeaderProbe reads channels/rate/duration for WAV / FLAC / AIFF / MP3 and
 * returns null for "OGG / M4A / unknown". That null is cheap for OGG — the
 * browser decodes Vorbis/Opus in milliseconds — but it is expensive for exactly
 * the containers THIS module was written for: an E-AC-3 5.1 track in .m4a has to
 * go all the way through FFmpeg-WASM before anyone learns it has six channels.
 * Measured on /channel-converter with the reporter's own files (Beatles 2023
 * mixes, E-AC-3 5.1 in .m4a, 13-19 MB): 20-60 s to decode, ~6 ms to read `moov`.
 * The walk is already here; only the fields were missing.
 *
 * ⚠️ For Dolby AND for AAC the AudioSampleEntry `channelcount` is a placeholder
 * in several muxers — the truth is in the codec-specific box: `dac3`/`dec3`
 * (acmod + lfeon) for Dolby, `esds` → AudioSpecificConfig for AAC. Read from the
 * sample entry alone, a 5.1 E-AC-3 file AND a 5.1 AAC file both report 2.
 */
(function () {
    'use strict';

    /* Codecs a browser <video> can be expected to decode in-container. The
       list is deliberately a WHITELIST: an unknown id returns playable:null
       (don't know) rather than false, so a new codec never breaks playback. */
    var PLAYABLE = {
        'aac': 1, 'mp4a': 1, 'mp3': 1, 'mp2': 1, 'opus': 1, 'vorbis': 1,
        'flac': 1, 'pcm': 1, 'alac': 0   /* alac: Safari only → treat as unknown below */
    };
    /* Explicitly refused by Chromium and Firefox (measured, see header). */
    var REFUSED = { 'ac-3': 1, 'e-ac-3': 1, 'dts': 1, 'truehd': 1, 'mlp': 1 };

    function readSlice(file, start, end) {
        var s = Math.max(0, start);
        var e = Math.min(file.size, end);
        if (e <= s) return Promise.resolve(new Uint8Array(0));
        return file.slice(s, e).arrayBuffer().then(function (b) { return new Uint8Array(b); });
    }

    function u32(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }
    function str4(b, o) { return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]); }

    /* ── MP4 / MOV / M4V ──────────────────────────────────────────────────
       Walk TOP-LEVEL boxes by seeking — moov sits at the end in any file that
       was not written with +faststart, so reading "the first N MB" misses it
       on exactly the files most likely to be a download. */
    async function probeMp4(file) {
        var pos = 0, guard = 0;
        while (pos < file.size && guard++ < 512) {
            var head = await readSlice(file, pos, pos + 16);
            if (head.length < 8) break;
            var size = u32(head, 0), type = str4(head, 4), hdr = 8;
            if (size === 1) {                       // 64-bit largesize
                if (head.length < 16) break;
                var hi = u32(head, 8), lo = u32(head, 12);
                size = hi * 4294967296 + lo; hdr = 16;
            } else if (size === 0) {
                size = file.size - pos;             // "to end of file"
            }
            if (size < hdr) break;
            if (type === 'moov') {
                var moov = await readSlice(file, pos, pos + Math.min(size, 24 * 1024 * 1024));
                return scanMoov(moov);
            }
            pos += size;
        }
        return null;
    }

    /* Inside moov, find each trak whose hdlr is 'soun' and read its stsd
       sample-entry type. Nested walk over a buffer we already hold. */
    /* AC-3 / E-AC-3 acmod -> channel count (ETSI TS 102 366 table 4.3). */
    var ACMOD_CH = [2, 1, 2, 3, 3, 4, 4, 5];
    /* chan_loc bits, MSB first, and how many channels each one adds
       (ETSI TS 102 366 Annex F.3.1): a 7.1 E-AC-3 stream is a 5.1 core in the
       independent substream PLUS these, so a parser that stops at acmod reports
       6 for an 8-channel file. */
    var CHAN_LOC_ADD = [2, 2, 1, 1, 2, 2, 2, 1, 1];   // Lc/Rc, Lrs/Rrs, Cs, Ts, Lsd/Rsd, Lw/Rw, Lvh/Rvh, Cvh, LFE2

    function u16(b, o) { return (b[o] << 8) | b[o + 1]; }

    /* Tiny MSB-first bit reader — the Dolby config boxes are bit-packed and
       reading them with hand-rolled shifts is how the first version of this
       parser turned a correct 6 into a 2 (2026-08-29). */
    function bitsReader(b, o, len) {
        var bit = 0, end = len * 8;
        return function (n) {
            var v = 0;
            for (var k = 0; k < n; k++) {
                if (bit >= end) return v << (n - k - 1);
                v = (v << 1) | ((b[o + (bit >> 3)] >> (7 - (bit & 7))) & 1);
                bit++;
            }
            return v;
        };
    }

    /* dac3 (AC-3): fscod 2 | bsid 5 | bsmod 3 | acmod 3 | lfeon 1 | ... */
    function dac3Channels(b, o, len) {
        if (len < 2) return 0;
        var r = bitsReader(b, o, len);
        r(2); r(5); r(3);
        var acmod = r(3), lfeon = r(1);
        return ACMOD_CH[acmod] + lfeon;
    }

    /* dec3 (E-AC-3): data_rate 13 | num_ind_sub 3 | then, per independent
       substream: fscod 2 | bsid 5 | reserved 1 | asvc 1 | bsmod 3 | acmod 3 |
       lfeon 1 | reserved 3 | num_dep_sub 4 | (num_dep_sub ? chan_loc 9 : rsvd 1).
       Substream 0 carries the main programme; its dependent substreams carry the
       channels above the core, which is what makes 7.1 an 8 rather than a 6. */
    function dec3Channels(b, o, len) {
        if (len < 5) return 0;
        var r = bitsReader(b, o, len);
        r(13); r(3);                       // data_rate, num_ind_sub (substream 0 is enough)
        r(2); r(5); r(1); r(1); r(3);      // fscod, bsid, reserved, asvc, bsmod
        var acmod = r(3), lfeon = r(1);
        r(3);                              // reserved
        var numDep = r(4), extra = 0;
        if (numDep > 0) {
            var loc = r(9);
            for (var k = 0; k < 9; k++) if (loc & (1 << (8 - k))) extra += CHAN_LOC_ADD[k];
        }
        return ACMOD_CH[acmod] + lfeon + extra;
    }

    /* `tracks` — every sound trak in moov order, with its tkhd track_ID: a
       caller that has to take ONE track out (MediaAudioTrack) needs to know
       which, and the first is not always it. An iPhone «spatial audio» MOV
       carries an `apac` track next to the AAC one; nobody but Apple decodes it. */
    function scanMoov(b) {
        var codecs = [], traks = 0, tracks = [], trakId = 0;
        var info = { channels: 0, sampleRate: 0, durationSec: 0, bits: 0 };
        (function walk(start, end, inTrak, isSound) {
            var p = start;
            while (p + 8 <= end) {
                var size = u32(b, p), type = str4(b, p + 4), hdr = 8;
                if (size === 1) {
                    if (p + 16 > end) return;
                    size = u32(b, p + 8) * 4294967296 + u32(b, p + 12); hdr = 16;
                }
                if (size < hdr || p + size > end) {
                    if (size < hdr) return;
                    size = end - p;                  // truncated read — use what we have
                }
                var cs = p + hdr, ce = p + size;
                if (type === 'trak') {
                    traks++;
                    trakId = 0;
                    info.trakTs = 0;                 // this trak's own mdhd timescale (below)
                    // tkhd is a direct child: version(1) flags(3), then 4- or 8-byte
                    // creation/modification times, then track_ID.
                    for (var t = cs; t + 8 <= ce;) {
                        var tsz = u32(b, t);
                        if (tsz < 8 || t + tsz > ce) break;
                        if (str4(b, t + 4) === 'tkhd') {
                            var tv = b[t + 8], at = t + 8 + 4 + (tv === 1 ? 16 : 8);
                            if (at + 4 <= ce) trakId = u32(b, at);
                            break;
                        }
                        t += tsz;
                    }
                    var flag = { sound: false };
                    // first pass: is this a sound track?
                    (function findHdlr(s, e) {
                        var q = s;
                        while (q + 8 <= e) {
                            var sz = u32(b, q), ty = str4(b, q + 4);
                            if (sz < 8 || q + sz > e) return;
                            if (ty === 'hdlr' && q + 20 <= e && str4(b, q + 16) === 'soun') { flag.sound = true; return; }
                            if (ty === 'mdia' || ty === 'minf') findHdlr(q + 8, q + sz);
                            q += sz;
                        }
                    })(cs, ce);
                    walk(cs, ce, true, flag.sound);
                } else if (type === 'mdia' || type === 'minf' || type === 'stbl') {
                    walk(cs, ce, inTrak, isSound);
                } else if (type === 'stsd' && isSound) {
                    // stsd: version(4) entryCount(4) then sample entries
                    var q = cs + 8;
                    if (q + 8 <= ce) {
                        var entry = readAudioSampleEntry(b, q, ce, info);
                        codecs.push(entry.codec);
                        tracks.push({ id: trakId, codec: entry.codec, channels: entry.channels, sampleRate: entry.sampleRate });
                    }
                } else if (type === 'mdhd' && inTrak && isSound) {
                    // mdhd carries the track's own timescale + duration. Every sound
                    // trak's is read: `tracks` gives each its own rate (an `apac` field
                    // and its AAC need not share one); the file-level answers stay the
                    // first sound trak's.
                    var ver = b[cs], ts = 0, du = 0;
                    if (ver === 1 && cs + 28 <= ce) { ts = u32(b, cs + 20); du = u32(b, cs + 24) * 4294967296 + u32(b, cs + 28); }
                    else if (cs + 20 <= ce)          { ts = u32(b, cs + 12); du = u32(b, cs + 16); }
                    if (!info.durationSec && ts > 0 && du > 0 && du !== 0xFFFFFFFF) info.durationSec = du / ts;
                    if (ts > 0) info.trakTs = ts;
                    // The timescale of a sound track is its sample rate in every muxer
                    // measured — and the one place a rate above 65 535 survives (see
                    // readAudioSampleEntry). mdhd precedes stsd inside the trak.
                    if (ts > 0 && !info.timescale) info.timescale = ts;
                }
                p += size;
            }
        })(8, b.length, false, false);
        /* A moov that holds tracks and none of them `soun` is an ANSWER — the
           movie has no sound track — not a failure to read. Until 25.09.2026
           both came back null, and probe() reported both as `hasAudio:false,
           reason:'unreadable'`: «no sound» and «could not look» were one value,
           so no caller could trust either. Without a single trak we still
           cannot tell a silent movie from a header we failed to parse. */
        if (!codecs.length) {
            return traks > 0
                ? { codecs: [], tracks: [], source: 'mp4', channels: 0, sampleRate: 0, durationSec: 0, bits: 0 }
                : null;
        }
        return { codecs: codecs, tracks: tracks, source: 'mp4', channels: info.channels,
                 sampleRate: info.sampleRate, durationSec: info.durationSec, bits: info.bits };
    }

    /* AAC says its real channel count in the AudioSpecificConfig inside `esds`,
       NOT in the sample entry — whose channelcount field is 2 for essentially
       every AAC track whatever the layout is. Measured 2026-09-11 on a 5.1 AAC
       file: sample entry 2, AudioSpecificConfig 6. This is the same placeholder
       problem `dac3`/`dec3` already solve for Dolby, in the container users are
       most likely to bring surround in.

       ASC channelConfiguration → channels. 0 means "a program config element
       decides", i.e. unknown from here — answer 0 and let the caller keep what
       it had. 7 is 7.1, which is EIGHT channels, not seven. */
    var ASC_CHANNELS = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 8 };

    /* MPEG-4 descriptor length: 7 bits per byte, high bit = "one more byte". */
    function descLen(b, i, end) {
        var len = 0, n = 0, c;
        do {
            if (i + n >= end) return null;
            c = b[i + n]; len = (len << 7) | (c & 0x7f); n++;
        } while ((c & 0x80) && n < 4);
        return { len: len, read: n };
    }

    function esdsChannels(b, off, size) { return esdsInfo(b, off, size).channels; }

    /* One walk of `esds` for everything it says: the stream type and the
       channel count. `oti` is the DecoderConfigDescriptor objectTypeIndication
       (-1 = not read), `aot` the AudioSpecificConfig object type (0 = not read).

       The type matters because the `mp4a` sample entry is NOT «AAC»: it is
       «MPEG audio described by esds», and MP3 in MP4 uses it too (OTI 0x6B /
       0x69). Read as AAC, an MP3-in-MP4 video went to the copy lane as a
       «.m4a» holding MP3 frames (corpus 2026-10-08). */
    function esdsInfo(b, off, size) {
        var out = { oti: -1, aot: 0, channels: 0 };
        try {
            var end = off + size, p = off + 4;               // skip FullBox version/flags
            if (p >= end || b[p] !== 0x03) return out;       // ES_Descriptor
            p++; var d = descLen(b, p, end); if (!d) return out; p += d.read;
            p += 2;                                          // ES_ID
            if (p >= end) return out;
            var flags = b[p++];
            if (flags & 0x80) p += 2;                        // streamDependence
            if (flags & 0x40) { if (p >= end) return out; p += 1 + b[p]; }   // URL
            if (flags & 0x20) p += 2;                        // OCR stream
            if (p >= end || b[p] !== 0x04) return out;       // DecoderConfigDescriptor
            p++; d = descLen(b, p, end); if (!d) return out; p += d.read;
            if (p < end) out.oti = b[p];
            p += 13;                                         // objectType .. avgBitrate
            if (p >= end || b[p] !== 0x05) return out;       // DecoderSpecificInfo = ASC
            p++; d = descLen(b, p, end); if (!d) return out; p += d.read;
            if (p + 2 > end) return out;
            var bitPos = 0;
            var take = function (n) {
                var v = 0;
                for (var i = 0; i < n; i++) {
                    var byteI = p + (bitPos >> 3);
                    if (byteI >= end) return -1;
                    v = (v << 1) | ((b[byteI] >> (7 - (bitPos & 7))) & 1);
                    bitPos++;
                }
                return v;
            };
            var aot = take(5);
            if (aot === 31) aot = 32 + take(6);              // escape value
            out.aot = aot > 0 ? aot : 0;
            var fi = take(4);
            if (fi === 15) take(24);                         // explicit sample rate
            var cfg = take(4);                               // channelConfiguration
            out.channels = (cfg > 0 && ASC_CHANNELS[cfg]) ? ASC_CHANNELS[cfg] : 0;
            return out;
        } catch (_) { return out; }
    }

    /* objectTypeIndication → our codec id (ISO 14496-1 Table 5 + the MP4RA
       registry). An OTI we do not know keeps the old answer, 'aac' — the
       sample entry said `mp4a` and AAC is what that overwhelmingly carries. */
    function codecOfOti(oti, aot) {
        if (oti === 0x40) return (aot >= 32 && aot <= 34) ? 'mp3' : 'aac';   // MPEG-4 Audio; AOT 32–34 = Layer 1/2/3
        if (oti === 0x66 || oti === 0x67 || oti === 0x68) return 'aac';     // MPEG-2 AAC Main / LC / SSR
        if (oti === 0x69 || oti === 0x6B) return 'mp3';                    // MPEG-2 / MPEG-1 audio (Layer 3, also 1/2)
        if (oti === 0xA5) return 'ac-3';
        if (oti === 0xA6) return 'e-ac-3';
        if (oti === 0xA9 || oti === 0xAA || oti === 0xAB || oti === 0xAC) return 'dts';
        if (oti === 0xAD) return 'opus';
        if (oti === 0xDD) return 'vorbis';
        return 'aac';
    }

    /* AudioSampleEntry (ISO 14496-12 §12.2.3):
         6 reserved | 2 data_ref_index | 2 version | 2 revision | 4 vendor
         2 channelcount | 2 samplesize | 2 pre_defined | 2 reserved | 4 samplerate(16.16)
       then codec-specific boxes. QuickTime sound v2 restates rate/channels as
       float64/uint32 after the v1 block; v1 adds 16 bytes but keeps v0's fields. */
    function readAudioSampleEntry(b, q, ce, info) {
        var esize = u32(b, q);
        var codec = normalizeMp4(str4(b, q + 4)), mp4a = codec === 'aac';
        if (q + 36 > ce) return { codec: codec, channels: 0, sampleRate: 0 };
        var sver = u16(b, q + 16);
        var ch = u16(b, q + 24), bits = u16(b, q + 26), rate = u16(b, q + 32);
        var extra = (sver === 1) ? 16 : 0;
        if (sver === 2 && q + 52 <= ce) {
            try {
                var dv = new DataView(b.buffer, b.byteOffset + q + 36, 20);
                var r2 = dv.getFloat64(4), c2 = dv.getUint32(12);
                if (r2 > 0) rate = Math.round(r2);
                if (c2 > 0) ch = c2;
            } catch (_) {}
            extra = 36;
        }
        // Dolby: the sample entry's channelcount is a placeholder in several
        // muxers — dac3/dec3 carries the real bed. Scan the codec boxes.
        var p = q + 36 + extra, end = Math.min(q + Math.max(esize, 36), ce);
        while (p + 8 <= end) {
            var sz = u32(b, p), ty = str4(b, p + 4);
            if (sz < 8 || p + sz > end) break;
            var es = null;
            var real = (ty === 'dac3') ? dac3Channels(b, p + 8, sz - 8)
                     : (ty === 'dec3') ? dec3Channels(b, p + 8, sz - 8)
                     : (ty === 'esds') ? (es = esdsInfo(b, p + 8, sz - 8)).channels : 0;
            // QuickTime wraps the codec boxes in `wave`; look one level in so a
            // .mov-flavoured AAC track is read like an .m4a one.
            if (!real && ty === 'wave') {
                var w = p + 8, wend = p + sz;
                while (w + 8 <= wend) {
                    var wsz = u32(b, w), wty = str4(b, w + 4);
                    if (wsz < 8 || w + wsz > wend) break;
                    if (wty === 'esds') { es = esdsInfo(b, w + 8, wsz - 8); real = es.channels; break; }
                    w += wsz;
                }
            }
            if (mp4a && es && es.oti >= 0) codec = codecOfOti(es.oti, es.aot);
            // Only ever RAISE. The reason this override exists is the Atmos/Apple
            // placeholder, where the sample entry under-reports (2 for a 5.1 bed).
            // Lowering is the direction that can only lose information, and it is
            // the direction that broke this parser the first time it was written.
            if (real > ch) ch = real;
            p += sz;
        }
        // The v0/v1 field is 16.16: sixteen bits of integer, so a rate above 65 535
        // does not fit. FFmpeg writes 0 there; other muxers keep the low 16 bits —
        // 96 000 arrives as 30 464, and on production a 96 kHz m4a was taken for a
        // 30 kHz file (/denoise, 08.10.2026). The track's timescale holds the real
        // rate (measured on FFmpeg's own m4a at 88.2 / 96 / 176.4 / 192 kHz), so when
        // the field is empty or is exactly the timescale's low 16 bits, the
        // timescale is the answer. A v2 entry carries the rate as float64 — read above.
        var tsc = info.trakTs || info.timescale;   // this trak's, else the first sound trak's
        if (sver !== 2 && tsc > 65535 && tsc <= 768000
            && (rate === 0 || rate === (tsc & 0xFFFF))) rate = tsc;
        if (!info.channels && ch > 0) info.channels = ch;
        if (!info.sampleRate && rate > 0) info.sampleRate = rate;
        // Only uncompressed / lossless entries carry a meaningful word length;
        // for a lossy codec `samplesize` is a fixed 16 that means nothing.
        var cc = String(str4(b, q + 4)).toLowerCase();
        if (!info.bits && bits > 0 && /lpcm|sowt|twos|in24|in32|alac|fl32|fl64|raw /.test(cc)) info.bits = bits;
        return { codec: codec, channels: ch > 0 ? ch : 0, sampleRate: rate > 0 ? rate : 0 };
    }

    function normalizeMp4(fourcc) {
        var f = String(fourcc).toLowerCase().replace(/\0/g, '');
        if (f === 'mp4a') return 'aac';
        if (f === 'ac-3' || f === 'ac3') return 'ac-3';
        if (f === 'ec-3' || f === 'eac3') return 'e-ac-3';
        if (f === 'dtsc' || f === 'dtse' || f === 'dtsh' || f === 'dtsl') return 'dts';
        if (f === 'mlpa') return 'truehd';
        if (f === 'alac') return 'alac';
        if (f === 'fLaC'.toLowerCase() || f === 'flac') return 'flac';
        if (f === 'opus') return 'opus';
        if (f === '.mp3' || f === 'mp3 ') return 'mp3';
        if (f === 'sowt' || f === 'twos' || f === 'lpcm' || f === 'in24' || f === 'raw ') return 'pcm';
        return f;
    }

    /* ── Matroska / WebM ──────────────────────────────────────────────────
       Locate the Tracks element (id 0x1654AE6B) by a bounded byte scan, then
       read every TrackEntry's CodecID where TrackType == 2 (audio). A full
       EBML parser is not warranted here: we only need the codec strings, and
       CodecID values are ASCII and self-delimiting. */
    var TRACKS_ID = [0x16, 0x54, 0xAE, 0x6B];
    var INFO_ID   = [0x15, 0x49, 0xA9, 0x66];
    var SCAN_MB = 8 * 1024 * 1024;

    /* Minimal EBML reader — enough for Tracks and Info, which is all this
       module asks of the container. Returns {id, dataStart, dataEnd, next}. */
    function ebmlRead(b, p, end) {
        if (p >= end) return null;
        var i, idLen = 0, first = b[p];
        for (i = 0; i < 4; i++) if (first & (0x80 >> i)) { idLen = i + 1; break; }
        if (!idLen || p + idLen > end) return null;
        var id = 0;
        for (i = 0; i < idLen; i++) id = (id * 256) + b[p + i];
        var q = p + idLen;
        if (q >= end) return null;
        var szFirst = b[q], szLen = 0;
        for (i = 0; i < 8; i++) if (szFirst & (0x80 >> i)) { szLen = i + 1; break; }
        if (!szLen || q + szLen > end) return null;
        var size = szFirst & (0xFF >> szLen), unknown = (size === (0xFF >> szLen));
        for (i = 1; i < szLen; i++) { size = size * 256 + b[q + i]; unknown = unknown && b[q + i] === 0xFF; }
        var ds = q + szLen;
        var de = unknown ? end : Math.min(end, ds + size);
        return { id: id, dataStart: ds, dataEnd: de, next: de, unknown: unknown, size: size };
    }
    function ebmlUint(b, s, e) { var v = 0; for (var i = s; i < e; i++) v = v * 256 + b[i]; return v; }
    function ebmlFloat(b, s, e) {
        try {
            var dv = new DataView(b.buffer, b.byteOffset + s, e - s);
            if (e - s === 4) return dv.getFloat32(0);
            if (e - s === 8) return dv.getFloat64(0);
        } catch (_) {}
        return 0;
    }
    function ebmlStr(b, s, e) {
        var t = '';
        for (var i = s; i < e && b[i]; i++) t += String.fromCharCode(b[i]);
        return t;
    }

    /* Walk TrackEntry (0xAE) children: TrackType 0x83, CodecID 0x86,
       Audio 0xE1 { Channels 0x9F, SamplingFrequency 0xB5 }. */
    function scanTracks(b, s, e) {
        var codecs = [], first = null, n, entries = 0;
        var p = s;
        while (p < e && (n = ebmlRead(b, p, e))) {
            if (n.id === 0xAE) {
                entries++;
                var t = { type: 0, codec: '', ch: 0, rate: 0 }, q = n.dataStart, c;
                while (q < n.dataEnd && (c = ebmlRead(b, q, n.dataEnd))) {
                    if (c.id === 0x83) t.type = ebmlUint(b, c.dataStart, c.dataEnd);
                    else if (c.id === 0x86) t.codec = ebmlStr(b, c.dataStart, c.dataEnd);
                    else if (c.id === 0xE1) {
                        var a = c.dataStart, ac;
                        while (a < c.dataEnd && (ac = ebmlRead(b, a, c.dataEnd))) {
                            if (ac.id === 0x9F) t.ch = ebmlUint(b, ac.dataStart, ac.dataEnd);
                            else if (ac.id === 0xB5) t.rate = Math.round(ebmlFloat(b, ac.dataStart, ac.dataEnd));
                            a = ac.next;
                        }
                    }
                    q = c.next;
                }
                if (t.type === 2) {
                    /* An audio track whose codec this table does not name is
                       still an audio track: dropping it made a file with sound
                       read as «no sound». Carry the raw id, lower-cased — the
                       verdict treats an unknown codec as `playable:null`. */
                    var norm = normalizeMkv(t.codec) || String(t.codec || 'unknown').toLowerCase();
                    if (codecs.indexOf(norm) < 0) codecs.push(norm);
                    if (!first) first = t;
                }
            }
            p = n.next;
        }
        // Tracks read, none of them audio → [] («no sound track»); no TrackEntry
        // at all → null («could not look»), exactly as scanMoov distinguishes.
        if (codecs.length) return { codecs: codecs, first: first };
        return entries > 0 ? { codecs: [], first: null } : null;
    }

    var SEGMENT_ID = 0x18538067, EBML_ID = 0x1A45DFA3;
    var TRACKS_EID = 0x1654AE6B, INFO_EID = 0x1549A966, CLUSTER_EID = 0x1F43B675;

    /* Read one element HEADER at `pos` in the file (never the payload). Returns
       {id, dataStart, size, unknownSize} — enough to descend or to seek past. */
    async function ebmlHeaderAt(file, pos) {
        var b = await readSlice(file, pos, Math.min(file.size, pos + 12));
        var h = ebmlRead(b, 0, b.length);
        if (!h) return null;
        // h.dataEnd is clamped to the 12-byte window we read — the element's real
        // length is h.size, and reading the clamped one made every element look
        // 12 bytes long (the tree walk then found nothing at all).
        return { id: h.id, dataStart: pos + h.dataStart, size: h.size, unknownSize: !!h.unknown };
    }

    /* Matroska is a tree, so walk the tree: EBML header, then Segment, then
       Segment's children until Info and Tracks are in hand. Info/Tracks sit
       before the Clusters in every file a muxer writes in one pass, so this
       reads a few hundred bytes and stops. Falling back to a byte scan is only
       for the files that break that order. */
    async function probeMkvTree(file) {
        var pos = 0, guard = 0, segEnd = file.size, inSegment = false;
        var res = { codecs: null, channels: 0, sampleRate: 0, durationSec: 0 };
        while (pos < file.size && guard++ < 4096) {
            var h = await ebmlHeaderAt(file, pos);
            if (!h) return null;
            var end = h.unknownSize ? file.size : Math.min(file.size, h.dataStart + h.size);
            if (h.id === EBML_ID) { pos = end; continue; }
            if (h.id === SEGMENT_ID) { inSegment = true; segEnd = end; pos = h.dataStart; continue; }
            if (!inSegment) { pos = end; continue; }
            if (h.id === INFO_EID || h.id === TRACKS_EID) {
                var body = await readSlice(file, h.dataStart, Math.min(end, h.dataStart + 4 * 1024 * 1024));
                if (h.id === TRACKS_EID) {
                    var t = scanTracks(body, 0, body.length);
                    if (t) {
                        res.codecs = t.codecs;
                        res.channels = (t.first && t.first.ch) || 0;
                        res.sampleRate = (t.first && t.first.rate) || 0;
                    }
                } else {
                    var scale = 1000000, dur = 0, q = 0, c;
                    while (q < body.length && (c = ebmlRead(body, q, body.length))) {
                        if (c.id === 0x2AD7B1) scale = ebmlUint(body, c.dataStart, c.dataEnd) || scale;
                        else if (c.id === 0x4489) dur = ebmlFloat(body, c.dataStart, c.dataEnd);
                        q = c.next;
                    }
                    if (dur > 0) res.durationSec = dur * scale / 1e9;
                }
            }
            // Clusters are the payload — everything we want precedes them.
            if (h.id === CLUSTER_EID) break;
            if (end <= pos) break;
            pos = end;
            if (pos >= segEnd) break;
        }
        return res.codecs ? res : null;
    }

    async function probeMkv(file) {
        try {
            var tree = await probeMkvTree(file);
            if (tree) {
                return { codecs: tree.codecs, source: 'mkv', channels: tree.channels,
                         sampleRate: tree.sampleRate, durationSec: tree.durationSec };
            }
        } catch (_) { /* fall through to the scan */ }

        // Fallback: Tracks trails the Clusters, or the tree walk hit something it
        // could not parse. Codec ids are ASCII, so pick them out of the text —
        // this can name the codec but can never answer the layout question.
        var head = await readSlice(file, 0, Math.min(file.size, SCAN_MB));
        var at = indexOfSeq(head, TRACKS_ID, 0);
        var buf = head;
        if (at < 0 && file.size > SCAN_MB) {
            buf = await readSlice(file, file.size - SCAN_MB, file.size);
            at = indexOfSeq(buf, TRACKS_ID, 0);
        }
        if (at < 0) return null;
        var region = buf.subarray(at, Math.min(buf.length, at + 4 * 1024 * 1024));
        var codecs = [];
        var text = '';
        for (var i = 0; i < region.length; i++) {
            var cb = region[i];
            text += (cb >= 32 && cb < 127) ? String.fromCharCode(cb) : ' ';
        }
        var re = /A_([A-Z0-9\/_]+)/g, m;
        while ((m = re.exec(text)) !== null) {
            var n2 = normalizeMkv('A_' + m[1]);
            if (n2 && codecs.indexOf(n2) < 0) codecs.push(n2);
        }
        return codecs.length
            ? { codecs: codecs, source: 'mkv', channels: 0, sampleRate: 0, durationSec: 0 }
            : null;
    }

    function indexOfSeq(buf, seq, from) {
        outer: for (var i = from; i <= buf.length - seq.length; i++) {
            for (var j = 0; j < seq.length; j++) if (buf[i + j] !== seq[j]) continue outer;
            return i;
        }
        return -1;
    }

    function normalizeMkv(id) {
        var s = String(id).toUpperCase();
        if (s.indexOf('A_AAC') === 0) return 'aac';
        if (s.indexOf('A_AC3') === 0) return 'ac-3';
        if (s.indexOf('A_EAC3') === 0) return 'e-ac-3';
        if (s.indexOf('A_DTS') === 0) return 'dts';
        if (s.indexOf('A_TRUEHD') === 0 || s.indexOf('A_MLP') === 0) return 'truehd';
        if (s.indexOf('A_OPUS') === 0) return 'opus';
        if (s.indexOf('A_VORBIS') === 0) return 'vorbis';
        if (s.indexOf('A_FLAC') === 0) return 'flac';
        if (s.indexOf('A_PCM') === 0) return 'pcm';
        if (s === 'A_MPEG/L3') return 'mp3';
        if (s === 'A_MPEG/L2') return 'mp2';
        if (s.indexOf('A_MPEG') === 0) return 'mp3';
        return '';
    }

    /* Ask the engine, but only believe a NO. WebKit answers "probably" to
       DTS and TrueHD, which it cannot decode — a yes here is not evidence. */
    function engineRefuses(codec) {
        try {
            var v = document.createElement('video');
            var t = {
                'ac-3': 'audio/mp4; codecs="ac-3"',
                'e-ac-3': 'audio/mp4; codecs="ec-3"',
                'dts': 'audio/mp4; codecs="dtsc"',
                'truehd': 'audio/mp4; codecs="mlpa"'
            }[codec];
            if (!t) return false;
            return v.canPlayType(t) === '';
        } catch (_) { return false; }
    }

    function verdict(codecs) {
        if (!codecs || !codecs.length) return { hasAudio: false, codecs: [], playable: null, reason: 'no_audio_track' };
        // A file with several audio tracks is playable if the FIRST (default)
        // one is — that is the track a native <video> will pick.
        var first = codecs[0];
        if (REFUSED[first] && engineRefuses(first)) {
            return { hasAudio: true, codecs: codecs, playable: false, reason: first };
        }
        if (PLAYABLE[first] === 1) return { hasAudio: true, codecs: codecs, playable: true, reason: first };
        return { hasAudio: true, codecs: codecs, playable: null, reason: first };
    }

    /* `hasAudio` is a boolean ONLY when the container was actually read:
       true = it holds a sound track (codec known or not), false = it was read
       and holds none. Anything we could not look into — AVI, MPEG-TS, a
       truncated header — is `null`, «don't know». Until 25.09.2026 those came
       back `false`, and every caller testing `hasAudio === false` threw away
       the sound of a file it merely failed to parse. */
    async function probe(file) {
        if (!file || !file.slice) return { hasAudio: null, codecs: [], playable: null, reason: 'no_file' };
        try {
            var sig = await readSlice(file, 0, 16);
            var res = null;
            if (sig.length >= 8 && str4(sig, 4) === 'ftyp') res = await probeMp4(file);
            else if (sig[0] === 0x1A && sig[1] === 0x45 && sig[2] === 0xDF && sig[3] === 0xA3) res = await probeMkv(file);
            else if (sig.length >= 8) {
                var box = str4(sig, 4);
                if (box === 'moov' || box === 'mdat' || box === 'wide' || box === 'free' || box === 'skip') res = await probeMp4(file);
            }
            if (!res) return { hasAudio: null, codecs: [], playable: null, reason: 'unreadable', source: '', channels: 0, sampleRate: 0, durationSec: 0 };
            var out = verdict(res.codecs);
            out.source = res.source;
            // 0 = the header did not say. Never "none" — a caller that reads 0 as
            // "mono" or "empty" turns a missing field into a wrong answer.
            out.channels    = res.channels   || 0;
            out.sampleRate  = res.sampleRate || 0;
            out.durationSec = res.durationSec || 0;
            if (res.bits) out.bits = res.bits;
            if (res.tracks) out.tracks = res.tracks;
            return out;
        } catch (_) {
            return { hasAudio: null, codecs: [], playable: null, reason: 'error', source: '', channels: 0, sampleRate: 0, durationSec: 0 };
        }
    }

    window.MediaAudioProbe = {
        probe: probe,
        _scanMoov: scanMoov,
        _normalizeMkv: normalizeMkv,
        _normalizeMp4: normalizeMp4,
        _verdict: verdict,
        _scanTracks: scanTracks,
        // Exposed for the spec-case tests: the Dolby config boxes are bit-packed
        // and an off-by-two here silently rewrites a file's layout.
        _dec3Channels: dec3Channels,
        _dac3Channels: dac3Channels,
        REFUSED: REFUSED
    };
})();
